import {cleanText, dialogKey, isLoading, isVisible, readPage} from "@/functions/webMcpDom";
import {registeredToolNames} from "@/functions/webMcp";
import {isPreparerContext} from "@/functions/getPreparerContext";
import getBrandName from "@/functions/getBrandName";
import {user} from "@/functions/user";
import {session} from "@/functions/authenticatedRequest";

const SIGN_IN = 'The person signs in with their username and password or a passkey.'
const CODE = 'A verification code was sent. Ask the person for it, or use request_verification_help for another way to get one.'
const SIGNED_IN = 'The person is signed in and managing their account.'
const SIGN_IN_UNCONFIRMED = 'An account page; whether the person is signed in has not been confirmed yet, and get_auth_state checks it.'
const SIGN_IN_REQUIRED = {step: 'sign_in_required', category: 'sign_in_required', about: 'This account page needs the person to be signed in, and they are not. Call navigate with sign_in, help them sign in, then open the page again.'}

const STEP_GUIDE = {
	Login: {step: 'sign_in', about: SIGN_IN},
	LoginNew: {step: 'sign_in', about: `${SIGN_IN} New users can create an account instead.`},
	LoginSso: {step: 'sign_in', about: 'Single sign-on sign in: enter the username first; the site then asks for a password or sends the person to their sign-in provider.'},
	SsoCallback: {step: 'finishing_single_sign_on', about: 'Finishing sign in with the partner provider. Wait for the next page.'},
	PartnerLanding: {step: 'leaving_for_partner', about: 'Sending the person to their partner sign-in provider.'},
	PartnerTransfer: {step: 'link_partner_account', about: 'The person signed in with a partner and chooses whether to link an existing account or create a new one.'},
	PartnerLinkComplete: {step: 'link_partner_account_result', about: 'Linking the partner account to the signed-in account.'},
	LoginDenied: {step: 'unsupported_browser', about: 'This browser or app is not supported. The person needs to open the site in a supported browser.'},
	About: {step: 'about', about: 'Information page.'},
	PageNotFound: {step: 'page_not_found', about: 'This page does not exist. Navigate to sign_in.'},
	ConfirmEmail: {step: 'confirm_email', about: 'Confirming the person\'s email address from an emailed link.'},
	VerifyWithTwoStepMethod: {step: 'choose_verification_method', about: 'Two-step verification: choose how the person receives a code.'},
	EmailVerification: {step: 'enter_email_code', about: CODE},
	TextVerification: {step: 'enter_phone_code', about: CODE},
	AuthAppVerification: {step: 'enter_authenticator_code', about: 'Enter the 6-digit code from the person\'s authenticator app.'},
	EmergencyCodeVerification: {step: 'enter_emergency_code', about: 'Enter one of the person\'s saved emergency codes.'},
	NewUser: {step: 'create_account', about: 'Create a new account.'},
	ChooseUsername: {step: 'choose_username', about: 'The person must choose a username before continuing.'},
	UpdatePassword: {step: 'update_password', about: 'The person must set a new password before continuing.'},
	PasskeyUpsell: {step: 'passkey_offer', about: 'The site offers to create a passkey. Creating one needs the person at their device; they can also skip it.'},
	ReclaimAccount: {step: 'recover_account', about: 'Account recovery: enter the email address or phone number on the account.'},
	ReclaimWithUsername: {step: 'recover_account', about: 'Account recovery: enter the username on the account.'},
	VerifySSN: {step: 'verify_identity_ssn', about: 'Account recovery: confirm identity with the Social Security number on the account.'},
	VerifyPTIN: {step: 'verify_identity_ptin', about: 'Account recovery for a tax preparer: confirm identity with the PTIN on the account.'},
	SelectAccount: {step: 'select_account', about: 'Several accounts matched. Choose the account to recover.'},
	SelectResetMethod: {step: 'choose_reset_method', about: 'Choose where to send the recovery code.'},
	EnterResetCode: {step: 'enter_reset_code', about: 'Enter the recovery code that was just sent.'},
	ChooseNewPassword: {step: 'choose_new_password', about: 'Set a new password for the recovered account.'},
	PasswordUpdated: {step: 'password_updated', about: 'The password was changed. The person can sign in now.'},
	EnterEmail: {step: 'recover_username_email', about: 'Recover a username: enter the email address on the account.'},
	EnterUsername: {step: 'recover_account_username', about: 'Enter the username on the account.'},
	UsernameEmailSent: {step: 'username_email_sent', about: 'The username was emailed to the person.'},
	AccountInfo: {step: 'account_overview', about: SIGNED_IN},
	AccountActivity: {step: 'account_activity', about: `${SIGNED_IN} Recent sign-in activity and trusted devices.`},
	EditUsername: {step: 'change_username', about: SIGNED_IN},
	EditPassword: {step: 'change_password', about: SIGNED_IN},
	EditEmail: {step: 'change_email', about: SIGNED_IN},
	EditSecondEmail: {step: 'change_second_email', about: SIGNED_IN},
	ConfirmDeleteSecondEmail: {step: 'remove_second_email', about: SIGNED_IN},
	EditMobilePhone: {step: 'change_phone', about: SIGNED_IN},
	ConfirmDeleteMobilePhone: {step: 'remove_phone', about: SIGNED_IN},
	VerifySMS: {step: 'verify_phone', about: `${SIGNED_IN} Enter the code sent to the new phone number.`},
	EditSecuritySettingsPassword: {step: 'unlock_security_settings', about: `${SIGNED_IN} The current password opens the security settings.`},
	EditSecuritySettings: {step: 'security_settings', about: SIGNED_IN},
	EnableAuthApp: {step: 'authenticator_app_setting', about: SIGNED_IN},
	EnableAuthAppQrCode: {step: 'authenticator_app_setup', about: `${SIGNED_IN} The person scans the QR code with their authenticator app, then enters the 6-digit code it shows.`},
	EnableAuthAppSuccess: {step: 'authenticator_app_enabled', about: `${SIGNED_IN} Emergency codes are showing on the page for the person to save.`},
	DisableAuthAppSuccess: {step: 'authenticator_app_disabled', about: SIGNED_IN},
	EnableEmergencyCodes: {step: 'emergency_codes_setting', about: SIGNED_IN},
	EmergencyCodes: {step: 'emergency_codes', about: `${SIGNED_IN} Emergency codes are showing on the page for the person to save.`},
	DisableEmergencyCodesSuccess: {step: 'emergency_codes_disabled', about: SIGNED_IN},
	EnableTextVerification: {step: 'sign_in_verification_settings', about: SIGNED_IN},
	Passkey: {step: 'manage_passkeys', about: SIGNED_IN},
	PreparerLogin: {step: 'preparer_sign_in', about: 'Tax preparer sign in.'},
	CreatePreparerAccount: {step: 'create_preparer_account', about: 'Create a tax preparer account.'},
	PreparerVerifyEmail: {step: 'preparer_verify_email', about: CODE},
	CreateClientAccount: {step: 'create_client_account', about: 'A signed-in tax preparer creates an account for a client.'},
	LinkClientAccount: {step: 'link_client_account', about: 'A tax preparer links a client\'s existing account using the client\'s username and password.'},
}

const CATEGORY_ROUTES = {
	sign_in: ['Login', 'LoginNew', 'LoginSso', 'LoginDenied', 'ChooseUsername', 'UpdatePassword', 'PasskeyUpsell'],
	create_account: ['NewUser', 'ConfirmEmail'],
	second_factor: ['VerifyWithTwoStepMethod', 'EmailVerification', 'TextVerification', 'AuthAppVerification', 'EmergencyCodeVerification'],
	account_recovery: ['ReclaimAccount', 'ReclaimWithUsername', 'SelectAccount', 'SelectResetMethod', 'EnterResetCode', 'ChooseNewPassword', 'PasswordUpdated', 'EnterEmail', 'EnterUsername', 'UsernameEmailSent'],
	identity_verification: ['VerifySSN', 'VerifyPTIN'],
	account_security: ['EditPassword', 'EditEmail', 'EditSecondEmail', 'ConfirmDeleteSecondEmail', 'EditMobilePhone', 'ConfirmDeleteMobilePhone', 'VerifySMS', 'EditSecuritySettingsPassword', 'EditSecuritySettings', 'EnableAuthApp', 'EnableAuthAppQrCode', 'EnableAuthAppSuccess', 'DisableAuthAppSuccess', 'EnableEmergencyCodes', 'EmergencyCodes', 'DisableEmergencyCodesSuccess', 'EnableTextVerification', 'Passkey'],
	account_info: ['AccountInfo', 'AccountActivity', 'EditUsername'],
	partner: ['SsoCallback', 'PartnerLanding', 'PartnerTransfer', 'PartnerLinkComplete'],
	preparer: ['PreparerLogin', 'CreatePreparerAccount', 'PreparerVerifyEmail', 'CreateClientAccount', 'LinkClientAccount'],
}

const CATEGORY_BY_ROUTE = Object.fromEntries(Object.entries(CATEGORY_ROUTES).flatMap(([category, names]) => names.map((name) => [name, category])))

export const CATEGORY_SENTENCES = {
	sign_in: 'Part of signing in: the person enters their own credentials, or finishes a change the site requires before continuing to the tax return.',
	create_account: 'Part of creating a new account; by creating one, the person agrees to the Terms of Use and Privacy Policy, which get_legal_document reads.',
	second_factor: 'Part of two-step verification after sign-in; the code goes to the person, so ask them for it rather than guessing or reusing an old one.',
	account_recovery: 'Part of recovering an account the person can\'t sign in to; codes and reset links go to the person\'s own email or phone.',
	identity_verification: 'Confirms the person\'s identity with the Social Security number or PTIN on the account; the number is never returned or logged.',
	account_security: 'Changes a sign-in or security setting on the signed-in account, such as email, phone, password, two-step verification, or passkeys.',
	account_info: 'Shows or changes account details for the signed-in person; nothing on this screen affects the tax return itself.',
	partner: 'Part of signing in through a partner provider and linking that sign-in to an account on this site; the person approves the link with their own credentials.',
	preparer: 'Part of the tax preparer portal, for preparers and the client accounts they manage.',
	other: 'An information screen with nothing to submit; use navigate to move on.',
}

export const categoryFor = (routeName) => CATEGORY_BY_ROUTE[routeName] ?? 'other'

const ACCOUNT_CATEGORIES = ['account_security', 'account_info']

export const isAccountRoute = (routeName) => ACCOUNT_CATEGORIES.includes(categoryFor(routeName))

const sessionGuide = (guide, category) => {
	if (!ACCOUNT_CATEGORIES.includes(category) || session.signedIn === true) return {...guide, category}
	if (session.signedIn === false) return SIGN_IN_REQUIRED
	return {...guide, category, about: guide.about.replace(SIGNED_IN, SIGN_IN_UNCONFIRMED)}
}

let currentRoute = null
let readyRoute = null
let readyDialog = ''
let passkeyPrompt = null

export const setCurrentRoute = (route) => {
	if (currentRoute !== route) passkeyPrompt = null
	currentRoute = route
}

export const getCurrentRoute = () => currentRoute

export const notePasskeyPrompt = () => {
	passkeyPrompt = {route: currentRoute}
}

export const markToolsReady = (route) => {
	if (currentRoute !== route) return
	readyRoute = route
	readyDialog = dialogKey()
}

export const isRouteReady = () => currentRoute !== null && readyRoute === currentRoute

export const isStepSettled = () => currentRoute !== null && readyRoute === currentRoute && readyDialog === dialogKey()

const stepFor = (routeName) => STEP_GUIDE[routeName] ?? {step: 'unknown', about: 'Read the page fields and buttons.'}

const SECOND_FACTOR_METHODS = {
	EmailVerification: 'email',
	TextVerification: 'text_or_call',
	AuthAppVerification: 'authenticator_app',
	EmergencyCodeVerification: 'emergency_code',
	PreparerVerifyEmail: 'email',
	VerifySMS: 'text_or_call',
}

const readCodeDestination = () => {
	const sentLine = [...document.querySelectorAll('#mainContentArea p')].find((element) => isVisible(element) && /sent (a|the|your)? ?(verification )?code/i.test(cleanText(element)))
	return cleanText(sentLine?.querySelector('.fw-bold')) || undefined
}

const describeSecondFactor = (routeName) => {
	const method = SECOND_FACTOR_METHODS[routeName]
	if (!method) return undefined
	return {method, sentTo: readCodeDestination()}
}

const describePasskey = (category) => {
	const promptShowing = passkeyPrompt?.route === currentRoute
	const signInAvailable = category === 'sign_in' && user.isPasskeyTurnedOn === true && typeof window.PublicKeyCredential !== 'undefined'
	if (!promptShowing && !signInAvailable) return undefined
	return {
		promptShowing,
		signInAvailable,
		note: promptShowing ? 'A passkey prompt was started. Only the person can approve it on their device; password sign-in still works.' : undefined,
	}
}

export const describeCurrentStep = () => {
	const routeName = currentRoute?.name ?? null
	const guide = sessionGuide(stepFor(routeName), categoryFor(routeName))
	const category = guide.category
	return {
		step: guide.step,
		category,
		about: guide.about,
		signedIn: isAccountRoute(routeName) ? session.signedIn : undefined,
		path: location.pathname,
		brand: getBrandName(),
		taxYear: sessionStorage.getItem('appYear') || process.env.VUE_APP_CURRENT_TAX_YEAR,
		preparerContext: isPreparerContext() || undefined,
		...readPage(),
		secondFactor: describeSecondFactor(routeName),
		passkey: describePasskey(category),
		toolsHere: registeredToolNames(),
		toolsUpdating: !isStepSettled() || undefined,
	}
}

const waitUntilIdle = (timeoutMs = 10000) => new Promise((resolve) => {
	const startedAt = Date.now()
	const timer = setInterval(() => {
		if (!isLoading() || Date.now() - startedAt > timeoutMs) {
			clearInterval(timer)
			resolve()
		}
	}, 100)
})

export const markStepReady = async (route) => {
	delete document.body.dataset.step
	delete document.body.dataset.route
	delete document.body.dataset.category
	await waitUntilIdle()
	if (currentRoute !== route) return false
	document.body.dataset.step = stepFor(route.name).step
	document.body.dataset.route = String(route.name ?? '')
	document.body.dataset.category = categoryFor(route.name)
	const status = document.getElementById('pageStatus')
	const heading = document.querySelector('#mainContentArea h1')?.textContent?.trim()
	if (status) status.textContent = heading ? `${heading} loaded` : 'Page loaded'
	return true
}